import React from 'react';
import {type IconProp} from '@fortawesome/fontawesome-svg-core';
import {
	Snackbar as MuiSnackbar,
	type SnackbarProps as MuiSnackbarProps,
	Slide,
	type SlideProps,
} from '@mui/material';
import {Alert, type AlertProps} from '@netx/core-components/Alert';
import {Button} from '@netx/core-components/Button';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';

type _MuiSnackbarProps = Required<MuiSnackbarProps>;

// handle snackbar transition into view
const TransitionUp = React.forwardRef<HTMLElement, SlideProps>((props, ref) => {
	return <Slide direction="up" ref={ref} {...props} />;
}) as (props: SlideProps) => JSX.Element;

export interface SnackbarProps
	extends MuiSnackbarProps,
		Pick<AlertProps, 'severity' | 'variant'> {
	preventCloseOnClickAway?: boolean;
	actionLabel?: string;
	actionIcon?: IconProp;
	actionCallback?: () => void;
}

export const SnackbarPrimitive = ({
	actionIcon,
	actionLabel,
	actionCallback,
	message,
	preventCloseOnClickAway,
	severity = 'info',
	variant = 'filled',
	onClose,
	...props
}: SnackbarProps) => {
	const handleClose = useConstCallback<_MuiSnackbarProps['onClose']>(
		(event, reason) => {
			if (!preventCloseOnClickAway || reason !== 'clickaway') {
				onClose?.(event, reason);
			}
		},
	);

	return (
		<MuiSnackbar
			anchorOrigin={{
				horizontal: 'center',
				vertical: 'bottom',
			}}
			autoHideDuration={6000}
			TransitionComponent={TransitionUp}
			{...props}
			onClose={handleClose}
			style={{
				cursor: props.onClick ? 'pointer' : undefined,
				...props.style,
			}}
		>
			<Alert
				onClose={(event) => {
					event.stopPropagation();
					handleClose(event, 'escapeKeyDown');
				}}
				severity={severity}
				variant={variant}
			>
				{message}
				{actionCallback && actionLabel ? (
					<Button
						variant="outlined"
						size="small"
						color="inherit"
						sx={{ml: 3}}
						startIcon={actionIcon}
						onClick={(event) => {
							actionCallback();
							handleClose(event, 'escapeKeyDown');
						}}
					>
						{actionLabel}
					</Button>
				) : null}
			</Alert>
		</MuiSnackbar>
	);
};

export const Snackbar = SnackbarPrimitive;

export default Snackbar;
